import React, { useState, useEffect } from "react";
import styled from "@emotion/styled";
import ModalLayout from "../../../components/structure/ModalLayout";
import ModalHeader from "../../../components/general/ModalHeader";
import FooterBar from "../../../components/structure/FooterBar";
import Button from "../../../components/general/Button";
import CustomScrollbars from "../../../components/structure/CustomScrollbars";
import useWindowSize from "../../../hooks/UseWindowSize";
import WebhookModel from "../../../models/webhooks/api/WebhookModel";
import { color_shades_dark, color_shades_darker, color_shades_light, color_text_default, color_text_light, silver_four } from "../../../constants/colors";
import { getConfigurationParameterNameFromOverrideKey, isConfigurationParameterOverrideKey, toConfigurationParameterOverrideKey } from "../../../utils/webhookParameterOverrides";

interface WebhookOverridesModalProps {
    isVisible: boolean;
    onClose: () => void;
    webhooks: WebhookModel[];
    applicationId: string;
    currentOverrides: { [webhookId: string]: string };
    currentParamOverrides: { [webhookId: string]: { [paramName: string]: string } };
    onSave: (
        urlOverrides: { [webhookId: string]: string },
        paramOverrides: { [webhookId: string]: { [paramName: string]: string } }
    ) => void;
}

const normalizeForComparison = (val: string | undefined | null) => {
    return (val ?? "")
        .replace(/\r\n/g, "\n")
        .replace(/\r/g, "\n")
        .trim();
};

const WebhookOverridesModal: React.FC<WebhookOverridesModalProps> = (props) => {
    const [overrides, setOverrides] = useState<{ [webhookId: string]: string }>({});
    const [paramOverrides, setParamOverrides] = useState<{ [webhookId: string]: { [paramName: string]: string } }>({});
    const [expandedWebhooks, setExpandedWebhooks] = useState<{ [webhookId: string]: boolean }>({});
    const windowSize = useWindowSize();
    const MODAL_SCROLLBAR_SUBTRACTION = 185;

    useEffect(() => {
        if (props.isVisible) {
            const initial: { [webhookId: string]: string } = {};
            props.webhooks?.forEach(wh => {
                initial[wh.id] = props.currentOverrides[wh.id] ?? wh.url ?? "";
            });
            setOverrides(initial);
            const initialParams: { [webhookId: string]: { [paramName: string]: string } } = {};
            props.webhooks?.forEach(wh => {
                const params: { [paramName: string]: string } = {};
                wh.parameters?.forEach(param => {
                    const currentValue = wh.parameterValuesForEdit?.find(pv => pv.webhookParameter?.name === param.name)?.value ?? param.defaultValue ?? "";
                    params[param.name] = props.currentParamOverrides?.[wh.id]?.[param.name] ?? currentValue;
                });
                wh.configurationParameters?.forEach(configParam => {
                    const overrideKey = toConfigurationParameterOverrideKey(configParam.key);
                    params[overrideKey] = props.currentParamOverrides?.[wh.id]?.[overrideKey] ?? configParam.value ?? "";
                });
                if (Object.keys(params).length > 0) {
                    initialParams[wh.id] = params;
                }
            });
            setParamOverrides(initialParams);
            setExpandedWebhooks({});
        }
    }, [props.isVisible, props.webhooks]);

    const handleOverrideChange = (webhookId: string, url: string) => {
        setOverrides(prev => ({ ...prev, [webhookId]: url }));
    };

    const handleParamOverrideChange = (webhookId: string, paramName: string, value: string) => {
        setParamOverrides(prev => ({
            ...prev,
            [webhookId]: {
                ...(prev[webhookId] ?? {}),
                [paramName]: value
            }
        }));
    };

    const toggleExpand = (webhookId: string) => {
        setExpandedWebhooks(prev => ({ ...prev, [webhookId]: !prev[webhookId] }));
    };

    const handleClearAll = () => {
        const reset: { [webhookId: string]: string } = {};
        props.webhooks?.forEach(wh => { reset[wh.id] = wh.url ?? ""; });
        setOverrides(reset);
        setParamOverrides({});
    };

    const handleSave = () => {
        const effectiveUrlOverrides: { [webhookId: string]: string } = {};
        Object.entries(overrides).forEach(([webhookId, url]) => {
            const webhook = props.webhooks?.find(wh => wh.id === webhookId);
            const trimmed = url?.trim() ?? "";
            if (trimmed.length > 0 && normalizeForComparison(trimmed) !== normalizeForComparison(webhook?.url)) {
                effectiveUrlOverrides[webhookId] = trimmed;
            }
        });

        const effectiveParamOverrides: { [webhookId: string]: { [paramName: string]: string } } = {};
        Object.entries(paramOverrides).forEach(([webhookId, params]) => {
            const webhook = props.webhooks?.find(wh => wh.id === webhookId);
            const overriddenParams: { [paramName: string]: string } = {};
            Object.entries(params ?? {}).forEach(([paramKey, value]) => {
                const trimmed = value?.trim() ?? "";
                if (trimmed.length === 0) return;
                if (isConfigurationParameterOverrideKey(paramKey)) {
                    const configKey = getConfigurationParameterNameFromOverrideKey(paramKey);
                    const configParam = webhook?.configurationParameters?.find(cp => cp.key === configKey);
                    const currentValue = configParam?.value ?? "";
                    if (normalizeForComparison(trimmed) !== normalizeForComparison(currentValue)) {
                        overriddenParams[paramKey] = trimmed;
                    }
                } else {
                    const param = webhook?.parameters?.find(p => p.name === paramKey);
                    const currentValue = webhook?.parameterValuesForEdit?.find(pv => pv.webhookParameter?.name === paramKey)?.value ?? param?.defaultValue ?? "";
                    if (normalizeForComparison(trimmed) !== normalizeForComparison(currentValue)) {
                        overriddenParams[paramKey] = trimmed;
                    }
                }
            });
            if (Object.keys(overriddenParams).length > 0) {
                effectiveParamOverrides[webhookId] = overriddenParams;
            }
        });

        props.onSave(effectiveUrlOverrides, effectiveParamOverrides);
        props.onClose();
    };

    const countOverriddenParams = (wh: WebhookModel) => {
        return Object.entries(paramOverrides[wh.id] ?? {}).filter(([paramKey, value]) => {
            const trimmed = value?.trim() ?? "";
            if (trimmed.length === 0) return false;
            if (isConfigurationParameterOverrideKey(paramKey)) {
                const configKey = getConfigurationParameterNameFromOverrideKey(paramKey);
                const configParam = wh.configurationParameters?.find(cp => cp.key === configKey);
                return normalizeForComparison(trimmed) !== normalizeForComparison(configParam?.value);
            } else {
                const param = wh.parameters?.find(p => p.name === paramKey);
                const currentValue = wh.parameterValuesForEdit?.find(pv => pv.webhookParameter?.name === paramKey)?.value ?? param?.defaultValue ?? "";
                return normalizeForComparison(trimmed) !== normalizeForComparison(currentValue);
            }
        }).length;
    };

    const activeOverrideCount = props.webhooks?.filter(wh => {
        const urlVal = overrides[wh.id]?.trim() ?? "";
        const urlOverridden = urlVal.length > 0 && normalizeForComparison(urlVal) !== normalizeForComparison(wh.url);
        return urlOverridden || countOverriddenParams(wh) > 0;
    }).length ?? 0;

    return (
        <ModalLayout isVisible={props.isVisible} onClose={props.onClose}>
            <ModalHeader
                title="Webhook Overrides"
                onClose={props.onClose}
                rightText={activeOverrideCount > 0 ? `${activeOverrideCount} overridden` : undefined}
            />
            <CustomScrollbars
                autoHide
                autoHeight
                autoHeightMax={(windowSize.windowSize.innerHeight * .9) - MODAL_SCROLLBAR_SUBTRACTION}
            >
                <Body>
                    <Description>
                        Override webhook URLs and parameters for simulator and test case requests.
                        These overrides are saved locally and will not affect live application behavior.
                    </Description>
                    {props.webhooks?.length > 0 ? (
                        <WebhookList>
                            {props.webhooks.map(webhook => {
                                const isUrlOverridden = (overrides[webhook.id]?.trim() ?? "") !== "" && normalizeForComparison(overrides[webhook.id]) !== normalizeForComparison(webhook.url);
                                const hasParams = (webhook.parameters?.length ?? 0) > 0 || (webhook.configurationParameters?.length ?? 0) > 0;
                                const isExpanded = !!expandedWebhooks[webhook.id];
                                const overriddenParamCount = countOverriddenParams(webhook);

                                return (
                                    <WebhookRow key={webhook.id}>
                                        <WebhookHeader>
                                            <WebhookTitle>
                                                {webhook.title}
                                                {isUrlOverridden && <OverrideLabel>URL Overridden</OverrideLabel>}
                                                {overriddenParamCount > 0 && <OverrideLabel>{overriddenParamCount} param{overriddenParamCount > 1 ? "s" : ""} overridden</OverrideLabel>}
                                            </WebhookTitle>
                                            {hasParams && (
                                                <ExpandButton onClick={() => toggleExpand(webhook.id)} type="button">
                                                    <ExpandChevron expanded={isExpanded}>▾</ExpandChevron>
                                                    {isExpanded ? "Hide" : "Show"} params
                                                </ExpandButton>
                                            )}
                                        </WebhookHeader>
                                        <OverrideInput
                                            type="text"
                                            placeholder="Enter URL"
                                            value={overrides[webhook.id] ?? ""}
                                            onChange={e => handleOverrideChange(webhook.id, e.target.value)}
                                        />
                                        {hasParams && isExpanded && (
                                            <ParamsSection>
                                                {(webhook.parameters?.length ?? 0) > 0 && (
                                                    <>
                                                        <ParamsSectionTitle>Instance Parameters</ParamsSectionTitle>
                                                        {webhook.parameters.map(param => {
                                                            const currentValue = webhook.parameterValuesForEdit?.find(pv => pv.webhookParameter?.name === param.name)?.value ?? param.defaultValue ?? "";
                                                            const overrideValue = paramOverrides[webhook.id]?.[param.name] ?? "";
                                                            const isParamOverridden = overrideValue.trim().length > 0 && normalizeForComparison(overrideValue) !== normalizeForComparison(currentValue);

                                                            return (
                                                                <ParamRow key={param.name}>
                                                                    <ParamLabel>
                                                                        {param.name}
                                                                        {param.isRequired && <RequiredMark>*</RequiredMark>}
                                                                        {isParamOverridden && <OverrideLabel>Overridden</OverrideLabel>}
                                                                    </ParamLabel>
                                                                    {param.description && <ParamDescription>{param.description}</ParamDescription>}
                                                                    <OverrideInput
                                                                        type="text"
                                                                        placeholder="Enter value"
                                                                        value={overrideValue}
                                                                        onChange={e => handleParamOverrideChange(webhook.id, param.name, e.target.value)}
                                                                    />
                                                                </ParamRow>
                                                            );
                                                        })}
                                                    </>
                                                )}
                                                {(webhook.configurationParameters?.length ?? 0) > 0 && (
                                                    <>
                                                        <ParamsSectionTitle>Configuration Parameters</ParamsSectionTitle>
                                                        {webhook.configurationParameters.map(configParam => {
                                                            const overrideKey = toConfigurationParameterOverrideKey(configParam.key);
                                                            const overrideValue = paramOverrides[webhook.id]?.[overrideKey] ?? "";
                                                            const isParamOverridden = overrideValue.trim().length > 0 && normalizeForComparison(overrideValue) !== normalizeForComparison(configParam.value);
                                                            return (
                                                                <ParamRow key={configParam.key}>
                                                                    <ParamLabel>
                                                                        {configParam.key}
                                                                        {isParamOverridden && <OverrideLabel>Overridden</OverrideLabel>}
                                                                    </ParamLabel>
                                                                    {configParam.description && <ParamDescription>{configParam.description}</ParamDescription>}
                                                                    <OverrideInput
                                                                        type="text"
                                                                        placeholder="Enter value"
                                                                        value={overrideValue}
                                                                        onChange={e => handleParamOverrideChange(webhook.id, overrideKey, e.target.value)}
                                                                    />
                                                                </ParamRow>
                                                            );
                                                        })}
                                                    </>
                                                )}
                                            </ParamsSection>
                                        )}
                                    </WebhookRow>
                                );
                            })}
                        </WebhookList>
                    ) : (
                        <EmptyState>
                            No webhooks are configured for this application.
                        </EmptyState>
                    )}
                </Body>
            </CustomScrollbars>
            <FooterBar>
                <Button
                    type="button"
                    themes={["secondary", "start"]}
                    text="Clear All"
                    onClick={handleClearAll}
                    disabled={activeOverrideCount === 0}
                />
                <Button
                    type="button"
                    themes={["primary", "end"]}
                    text="Save"
                    onClick={handleSave}
                />
            </FooterBar>
        </ModalLayout>
    );
};

export default WebhookOverridesModal;

const Body = styled.div`
    padding: 24px 32px;
`;

const Description = styled.p`
    font-family: Muli;
    font-size: 14px;
    line-height: 20px;
    color: ${color_text_light};
    margin: 0 0 24px 0;
`;

const WebhookList = styled.div`
    display: flex;
    flex-direction: column;
    gap: 16px;
`;

const WebhookRow = styled.div`
    border: 1px solid ${silver_four};
    border-radius: 8px;
    padding: 16px;
`;

const WebhookHeader = styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 8px;
`;

const WebhookTitle = styled.div`
    font-family: Muli;
    font-size: 14px;
    font-weight: 600;
    line-height: 20px;
    color: ${color_text_default};
    display: flex;
    align-items: center;
    gap: 8px;
    flex-wrap: wrap;
`;

const OverrideLabel = styled.span`
    font-size: 11px;
    font-weight: 600;
    line-height: 16px;
    color: #e67e22;
    background: #fef5ec;
    border-radius: 4px;
    padding: 1px 6px;
`;

const ExpandButton = styled.button`
    display: flex;
    align-items: center;
    gap: 4px;
    background: none;
    border: none;
    cursor: pointer;
    font-family: Muli;
    font-size: 12px;
    color: ${color_text_light};
    padding: 2px 6px;
    border-radius: 4px;
    white-space: nowrap;
    &:hover {
        background: ${color_shades_light};
        color: ${color_text_default};
    }
`;

const ExpandChevron = styled.span<{ expanded: boolean }>`
    display: inline-block;
    transition: transform 0.15s ease;
    transform: ${({ expanded }) => expanded ? "rotate(0deg)" : "rotate(-90deg)"};
    font-size: 14px;
    line-height: 1;
`;

const OverrideInput = styled.input`
    width: 100%;
    padding: 8px 12px;
    border: 1px solid ${color_shades_dark};
    border-radius: 4px;
    font-family: Muli;
    font-size: 14px;
    line-height: 20px;
    box-sizing: border-box;
    &:focus {
        outline: none;
        border-color: #3498db;
    }
    &::placeholder {
        color: ${color_shades_darker};
        font-style: italic;
    }
`;

const ParamsSection = styled.div`
    margin-top: 12px;
    border-top: 1px solid ${color_shades_light};
    padding-top: 12px;
    display: flex;
    flex-direction: column;
    gap: 12px;
`;

const ParamsSectionTitle = styled.div`
    font-family: Muli;
    font-size: 11px;
    font-weight: 700;
    letter-spacing: 0.5px;
    text-transform: uppercase;
    color: ${color_text_light};
`;

const ParamRow = styled.div`
    display: flex;
    flex-direction: column;
    gap: 4px;
`;

const ParamLabel = styled.div`
    font-family: Muli;
    font-size: 13px;
    font-weight: 600;
    color: ${color_text_default};
    display: flex;
    align-items: center;
    gap: 6px;
`;

const RequiredMark = styled.span`
    color: #e74c3c;
    font-size: 13px;
`;

const ParamDescription = styled.div`
    font-family: Muli;
    font-size: 12px;
    color: ${color_text_light};
`;


const EmptyState = styled.div`
    font-family: Muli;
    font-size: 14px;
    line-height: 20px;
    color: ${color_text_light};
    text-align: center;
    padding: 48px 0;
`;
